import { useState, useEffect } from 'react';
import { X } from 'lucide-react';

// Secure API endpoint - no more exposed webhook URLs!
const API_ENDPOINT = '/api/contact';

const ContactFormPopup = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setFormData({ name: '', email: '', message: '' });
      setShowSuccess(false);
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Send data to secure API endpoint
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout for server processing
      
      const response = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          userAgent: navigator.userAgent,
          referrer: document.referrer || 'direct'
        }),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);

      // Parse the response
      const result = await response.json();

      if (!response.ok) {
        // Handle different types of errors from the API
        const errorMessage = result.error || 'Failed to send message';
        throw new Error(errorMessage);
      }
      
      // Success! Show success message
      setIsSubmitting(false);
      setShowSuccess(true);
      
      // Close popup after success message
      setTimeout(() => {
        onClose();
      }, 2500);
      
    } catch (error) {
      console.error('Error submitting form:', error);
      setIsSubmitting(false);
      
      // Show user-friendly error message
      let errorMessage = 'Failed to send message. Please try again.';
      
      if (error.name === 'AbortError') {
        errorMessage = 'Request timed out. Please check your connection and try again.';
      } else if (error.message.includes('Missing required fields')) {
        errorMessage = 'Please fill in all required fields.';
      } else if (error.message.includes('Invalid email')) {
        errorMessage = 'Please enter a valid email address.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      alert(errorMessage);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      onClick={onClose}
    >
      {/* Backdrop with blur */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-md" />
      
      {/* Contact Form Container */}
      <div 
        className="animate-form-entrance relative w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Animated Border Container */}
        <div className="absolute -inset-[3px] overflow-hidden rounded-2xl">
          {/* Base gradient background */}
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-pink-500/20" />
          
          {/* Saber Energy Border */}
          <svg className="absolute inset-0 size-full">
            <defs>
              {/* Energy gradient that shifts colors */}
              <linearGradient id="energyBeam" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#3B82F6" stopOpacity="0" />
                <stop offset="10%" stopColor="#3B82F6" stopOpacity="0.3" />
                <stop offset="30%" stopColor="#8B5CF6" stopOpacity="0.6" />
                <stop offset="50%" stopColor="#EC4899" stopOpacity="1">
                  <animate attributeName="stop-color" 
                    values="#EC4899;#F59E0B;#EC4899" 
                    dur="2s" 
                    repeatCount="indefinite" />
                </stop>
                <stop offset="70%" stopColor="#8B5CF6" stopOpacity="0.6" />
                <stop offset="90%" stopColor="#3B82F6" stopOpacity="0.3" />
                <stop offset="100%" stopColor="#3B82F6" stopOpacity="0" />
              </linearGradient>
              
              {/* Intense glow filter */}
              <filter id="saberGlow">
                <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                <feGaussianBlur stdDeviation="6" result="coloredBlur2"/>
                <feMerge>
                  <feMergeNode in="coloredBlur2"/>
                  <feMergeNode in="coloredBlur"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
            </defs>
            
            {/* Outer glow layer */}
            <rect
              x="1"
              y="1"
              width="99%"
              height="99%"
              rx="16"
              fill="none"
              stroke="url(#energyBeam)"
              strokeWidth="6"
              strokeDasharray="120 280"
              filter="url(#saberGlow)"
              className="animate-saber-beam"
              opacity="0.6"
            />
            
            {/* Main energy beam */}
            <rect
              x="2"
              y="2"
              width="98%"
              height="98%"
              rx="15"
              fill="none"
              stroke="url(#energyBeam)"
              strokeWidth="3"
              strokeDasharray="100 300"
              filter="url(#saberGlow)"
              className="animate-saber-beam"
            />
            
            {/* Inner bright core */}
            <rect
              x="3"
              y="3"
              width="97%"
              height="97%"
              rx="14"
              fill="none"
              stroke="white"
              strokeWidth="1"
              strokeDasharray="60 340"
              className="animate-saber-beam"
              opacity="0.9"
            />
          </svg>
        </div>

        {/* Form Content */}
        <div className="relative rounded-2xl bg-gray-900/95 p-8 shadow-2xl backdrop-blur-xl">
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute right-4 top-4 text-gray-400 transition-colors duration-200 hover:text-white"
          >
            <X size={24} />
          </button>

          {/* Form Header */}
          <div className="mb-8 text-center">
            <h2 className="mb-2 text-3xl font-bold">
              <span className="animate-gradient bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                Get in touch
              </span>
            </h2>
            <p className="text-gray-400">I would love to hear from you</p>
          </div>

          {/* Success Message */}
          {showSuccess ? (
            <div className="py-12 text-center">
              <div className="mb-4">
                <svg className="mx-auto size-16 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="mb-2 text-xl font-semibold text-white">Message Sent!</h3>
              <p className="text-gray-400">Thank you for reaching out. I&apos;ll get back to you soon.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="Your Name"
                className="w-full rounded-lg border border-gray-700 bg-gray-800/50 px-4 py-3 text-white transition-all duration-200 placeholder:text-gray-500 focus:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-400/20"
              />

              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="Email address"
                className="w-full rounded-lg border border-gray-700 bg-gray-800/50 px-4 py-3 text-white transition-all duration-200 placeholder:text-gray-500 focus:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-400/20"
              />

              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                placeholder="Your message"
                rows={4}
                className="w-full resize-none rounded-lg border border-gray-700 bg-gray-800/50 px-4 py-3 text-white transition-all duration-200 placeholder:text-gray-500 focus:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-400/20"
              />

              <button
                type="submit"
                disabled={isSubmitting}
                className="group relative w-full overflow-hidden"
              >
                <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 transition-transform duration-300 group-hover:scale-105" />
                <div className="relative m-0.5 rounded-lg bg-gray-900 px-6 py-3 transition-all duration-300 group-hover:bg-transparent">
                  <span className="relative z-10 font-semibold text-white">
                    {isSubmitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="size-5 animate-spin" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Sending...
                      </span>
                    ) : (
                      'Send Message'
                    )}
                  </span>
                </div>
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactFormPopup;